<template>
	<CHeaderLayout @show-account-menu="$emit('show-account-menu')">
		<template #title>
			<h1>Email Domains</h1>
			<RouterLink
				v-if="model.EmailDomainsAreLocked || model.EmailDomainsLimit == 0"
				class="button button--secondary sz-2"
				:class="{ 'button--mobile-email-domain' : isMobile }"
				:to="`/${orgCode}/settings/plan/select?source=feature&details=customEmailDomain`"
			>
				Upgrade
			</RouterLink>
			<CButton
				v-else-if="model.EmailDomains.length < model.EmailDomainsLimit"
				class="button--secondary"
				:class="{'button--mobile-email-domain' : isMobile }"
				@click="showAddDomain = true"
			>
				<template #icon><Inline svg="plus" /></template>
				New Domain
			</CButton>
		</template>

		<main class="organization-settings-email">
			<OrganizationEmailDomainsView
				:model="model"
				:loading="loading"
				:org-code="orgCode"
				:verifying-domain="verifyingDomain"
				@verify-domain="handleVerifyDomain"
				@domain-settings="openDomainSettingsDialog"
				@add-domain="showAddDomain = true"
				@delete-domain="openDeleteDomainDialog"
			/>
			<CEmailDomainSettingsDialog
				:visible="showDomainSettings"
				:domain="currentDomain"
				@close="showDomainSettings = false"
			/>
			<CAddEmailDomainDialog
				:visible="showAddDomain"
				@close="showAddDomain = false"
				@submit="handleAddDomain"
			/>
			<CDeleteEmailDomainDialog
				:visible="showDeleteDomain"
				:domain-name="currentDomain.Id"
				@close="showDeleteDomain = false"
				@delete="handleDeleteDomain"
			/>
		</main>
	</CHeaderLayout>
</template>

<script lang="ts">
	import { defineComponent, onMounted, ref, reactive, computed, inject } from 'vue';
	import { getDomainSettings, addEmailDomain, deleteEmailDomain, verifyEmailDomain } from '@cognitoforms/api/services/settings/organization-settings-service';
	import type { EmailDomainSettingsViewModel } from '@cognitoforms/types/server-types/model/email-domain-settings-view-model';
	import type { EmailDomain } from '@cognitoforms/types/server-types/model/email-domain';
	import CHeaderLayout from 'src/components/app/HeaderLayout.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import OrganizationEmailDomainsView from './OrganizationEmailDomainsView.vue';
	import CAddEmailDomainDialog from './AddEmailDomainDialog.vue';
	import CDeleteEmailDomainDialog from './DeleteEmailDomainDialog.vue';
	import CEmailDomainSettingsDialog from './EmailDomainSettingsDialog.vue';
	import notify from 'src/utilities/notify';
	import { globalStore } from 'src/stores/global-store';
	import { isMobileKey } from 'src/utilities/injection-keys';

	export default defineComponent({
		name: 'OrganizationEmailDomains',
		components: {
			CHeaderLayout,
			CButton,
			OrganizationEmailDomainsView,
			CAddEmailDomainDialog,
			CDeleteEmailDomainDialog,
			CEmailDomainSettingsDialog
		},
		setup(props, { emit }) {
			const model = ref<EmailDomainSettingsViewModel>({
				EmailDomains: [],
				EmailDomainsLimit: 0,
				EmailDomainsAreLocked: false,
				BillingPlan: ''
			} as EmailDomainSettingsViewModel);
			const currentDomain = reactive<EmailDomain>({
				Id: '',
				IsVerified: false,
				Spf: '',
				SpfIsValid: false,
				Providers: [],
				LastVerified: null,
				Disabled: false
			});
			const loading = ref(true);
			const verifyingDomain = ref('');
			const showDomainSettings = ref(false);
			const showAddDomain = ref(false);
			const showDeleteDomain = ref(false);
			const refreshSettings = async () => { model.value = Object.assign({}, model.value, await getDomainSettings()); };
			const findDomainIndex = (domainName: string) => {
				return model.value.EmailDomains.findIndex((d: EmailDomain) => d.Id === domainName);
			};
			const openDomainSettingsDialog = (domain: EmailDomain) => {
				Object.assign(currentDomain, domain);
				showDomainSettings.value = true;
			};
			onMounted(async () => {
				await refreshSettings();
				loading.value = false;
				emit('component-loaded');
			});
			return {
				model,
				currentDomain,
				loading,
				verifyingDomain,
				showDomainSettings,
				showAddDomain,
				showDeleteDomain,
				orgCode: computed(() => globalStore.organization ? globalStore.organization.Code : ''),
				refreshSettings,
				findDomainIndex,
				openDomainSettingsDialog,
				async handleVerifyDomain(domain: EmailDomain) {
					verifyingDomain.value = domain.Id;
					try {
						const response = await verifyEmailDomain(domain.Id);
						const index = findDomainIndex(domain.Id);
						if (~index)
							Object.assign(model.value.EmailDomains[index], response);
					}
					catch {
					}
					verifyingDomain.value = null;
				},
				handleAddDomain: async (domainName: string) => {
					try {
						const response = await addEmailDomain(domainName);
						openDomainSettingsDialog(response);
						model.value.EmailDomains.push(response);
					}
					catch {
						notify('Failed to add email domain.', 'error');
					}
					showAddDomain.value = false;
				},
				openDeleteDomainDialog: (domain: EmailDomain) => {
					Object.assign(currentDomain, domain);
					showDeleteDomain.value = true;
				},
				handleDeleteDomain: async (domainName: string) => {
					try {
						await deleteEmailDomain(domainName);
						const index = findDomainIndex(domainName);
						if (~index)
							model.value.EmailDomains.splice(index, 1);
					}
					catch (e) {
						notify('Failed to delete email domain.', 'error');
					}
					showDeleteDomain.value = false;
				},
				isMobile: inject(isMobileKey)
			};
		},
		head() {
			return {
				title: 'Email Domains - Cognito Forms',
				titleTemplate: '%s'
			};
		}
	});
</script>

<style lang="scss">
.organization-settings-email {
	--content-max-width: 896px;

	.admin--mobile.admin--app-nav & {
		margin-top: 24px; // content needs more top spacing on mobile when header is not visible
	}
}

// buttons are outside of top level settings page class, so need this too
.button--mobile-email-domain {
	margin-top: 24px;
}
</style>